<template>
  <div class="cfg-tab">
    <div class="cfg-intro">
      <v-icon>mdi-information-outline</v-icon>
      <div>
        <p>
          Circuitul spune cine aprobă un demers și în ce ordine, înainte ca inginerul să-l execute. Fiecare tip de
          echipament are un circuit, iar o cauză poate avea propriul circuit (de exemplu „Încărcare toner” doar cu
          Șeful departamentului).
        </p>
        <p>O schimbare se aplică doar demersurilor noi; cele depuse merg mai departe pe circuitul cu care au fost depuse.</p>
      </div>
    </div>

    <div class="cfg-toolbar">
      <h3 class="panel-title">Circuite de aprobare <span class="count">{{ circuits.length }}</span></h3>
      <span class="grow-spacer" />
      <v-btn class="btn-green" @click="openCreate"><v-icon left>mdi-plus</v-icon>Circuit nou</v-btn>
    </div>

    <v-progress-linear v-if="loading && !loaded" indeterminate color="primary" class="mb-2" />
    <div class="cfg-list">
      <div v-if="loaded && !sorted.length" class="cfg-empty">Niciun circuit.</div>
      <div v-for="(c, i) in sorted" :key="c.id" class="cfg-row" :class="{ 'is-hidden': c.is_hidden }">
        <div class="cfg-order">
          <v-btn icon small :disabled="i === 0 || saving" aria-label="Mută mai sus" @click="move(c, -1)">
            <v-icon>mdi-chevron-up</v-icon>
          </v-btn>
          <v-btn icon small :disabled="i === sorted.length - 1 || saving" aria-label="Mută mai jos" @click="move(c, 1)">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>
        <div class="cfg-row__main">
          <div class="cfg-row__title">
            <span class="cfg-row__name">{{ c.name }}</span>
            <span v-if="c.is_hidden" class="cfg-tag"><v-icon>mdi-eye-off-outline</v-icon>Ascuns</span>
          </div>
          <CircuitPath class="circuits__path" :steps="c.steps" :review="c.rector_review" :label="`Traseul circuitului ${c.name}`" />
          <div class="cfg-row__meta">
            <span class="circuits__for">Folosit de:</span>
            <span v-for="t in c.types" :key="'t' + t.id" class="cfg-tag" :class="t.is_hidden ? '' : 'cfg-tag--sky'">
              <v-icon>mdi-shape-outline</v-icon>{{ t.name }}
            </span>
            <span v-for="x in c.causes" :key="'c' + x.id" class="cfg-tag" :class="x.is_hidden ? '' : 'cfg-tag--plum'">
              <v-icon>mdi-format-list-bulleted-type</v-icon>{{ x.name }}
            </span>
            <span v-if="!c.types.length && !c.causes.length" class="cfg-tag cfg-tag--amber">niciun tip sau cauză</span>
          </div>
          <div class="cfg-usage">{{ usageText(c.usage) || "Niciun demers depus pe acest circuit încă" }}</div>
        </div>
        <div class="cfg-row__actions">
          <v-btn icon color="primary" aria-label="Editează circuitul" @click="openEdit(c)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            :aria-label="c.is_hidden ? 'Arată circuitul' : 'Ascunde circuitul'"
            :title="c.is_hidden ? 'Arată din nou' : 'Ascunde'"
            :disabled="saving"
            @click="toggleHidden(c)"
          >
            <v-icon>{{ c.is_hidden ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
          </v-btn>
          <v-btn icon color="error" aria-label="Șterge circuitul" @click="remove(c)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog v-model="dialog" persistent scrollable max-width="760" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-form class="fdlg__form" @submit.prevent="save">
          <div class="fdlg__head">
            <div>
              <h2 class="fdlg__title">{{ editing ? "Editează circuitul" : "Circuit nou de aprobare" }}</h2>
              <p class="fdlg__sub">{{ editing ? editing.name : "Cine aprobă, în ce ordine, înainte de inginer." }}</p>
            </div>
            <v-btn icon aria-label="Închide" @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text ref="body" class="fdlg__body">
            <div v-if="editing && usedBy(editing).length" class="cfg-used-note">
              <v-icon small>mdi-alert-outline</v-icon>
              <p>
                Se aplică demersurilor noi pentru {{ usedBy(editing).join(", ") }}.
                {{ keptText(editing) }}
              </p>
            </div>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-form-textbox</v-icon>Denumire</div>
              <v-text-field
                v-model="form.name"
                label="Denumirea circuitului*"
                prepend-inner-icon="mdi-sitemap-outline"
                outlined
                dense
                maxlength="100"
                :error-messages="nameError"
                @input="nameError = ''"
              ></v-text-field>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-format-list-numbered</v-icon>Cine aprobă, în ordine</div>
              <p class="form-section__note">Inginerul tipului de echipament vine întotdeauna la final.</p>

              <ol v-if="form.steps.length" class="circuits__steps">
                <li v-for="(key, i) in form.steps" :key="key" class="circuits__step">
                  <span class="circuits__num">{{ i + 1 }}</span>
                  <v-icon class="circuits__icon">{{ stepOf(key).icon }}</v-icon>
                  <span class="circuits__text">
                    <span class="circuits__name">{{ stepOf(key).text }}</span>
                    <span class="circuits__hint">{{ stepOf(key).hint }}</span>
                  </span>
                  <span class="circuits__btns">
                    <v-btn icon small :disabled="i === 0" :aria-label="`Mută ${stepOf(key).text} mai sus`" @click="shift(i, -1)">
                      <v-icon>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn icon small :disabled="i === form.steps.length - 1" :aria-label="`Mută ${stepOf(key).text} mai jos`" @click="shift(i, 1)">
                      <v-icon>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon small color="error" :aria-label="`Scoate ${stepOf(key).text}`" @click="drop(i)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </span>
                </li>
              </ol>
              <div v-else class="circuits__none">
                <v-icon small>mdi-alert-outline</v-icon>Fără nicio aprobare: demersul merge direct la inginer.
              </div>

              <div v-if="available.length" class="circuits__add">
                <span class="circuits__add-label">Adaugă:</span>
                <v-btn v-for="s in available" :key="s.value" outlined small color="primary" class="circuits__add-btn" @click="add(s.value)">
                  <v-icon left small>mdi-plus</v-icon>{{ s.text }}
                </v-btn>
              </div>

              <v-switch
                v-model="form.rector_review"
                :disabled="!form.steps.includes('rector')"
                inset
                color="primary"
                class="mt-3 mb-1"
                label="Rectorul poate trimite „Pentru examinare” la Șeful DTI"
                :hint="form.steps.includes('rector')
                  ? 'Rectorul alege între „Aprobă cu executare” și „Pentru examinare”; după examinare, Șeful DTI trimite demersul mai departe.'
                  : 'Doar pentru circuitele cu Rector.'"
                persistent-hint
              ></v-switch>
            </section>

            <section class="form-section">
              <div class="form-section__label"><v-icon small>mdi-eye-outline</v-icon>Traseul</div>
              <CircuitPath :steps="form.steps" :review="form.rector_review && form.steps.includes('rector')" label="Traseul circuitului" />
            </section>
          </v-card-text>

          <div class="fdlg__foot">
            <v-spacer></v-spacer>
            <v-btn text large @click="dialog = false">Anulare</v-btn>
            <v-btn class="btn-green" large type="submit" :loading="saving">
              <v-icon left>mdi-content-save-outline</v-icon>Salvează
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <RemoveDialog ref="remove" />
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import configMixin from "./configMixin";
import RemoveDialog from "./RemoveDialog.vue";
import CircuitPath from "@/components/requests/CircuitPath.vue";
import { CIRCUIT_STEPS } from "@/config/circuits";
import { bySortOrder, cleanText, orderPatches, usageText } from "./format";

export default {
  name: "CircuitsTab",
  components: { RemoveDialog, CircuitPath },
  mixins: [configMixin],
  data() {
    return {
      circuits: [],
      dialog: false,
      editing: null,
      form: { name: "", steps: [], rector_review: false },
      nameError: "",
    };
  },
  computed: {
    sorted() {
      return [...this.circuits].sort(bySortOrder);
    },
    available() {
      return CIRCUIT_STEPS.filter((s) => !this.form.steps.includes(s.value));
    },
  },
  methods: {
    usageText,
    async load() {
      this.circuits = await api.getCircuits();
    },
    stepOf(key) {
      return CIRCUIT_STEPS.find((s) => s.value === key) || { text: key, icon: "mdi-account-outline", hint: "" };
    },
    // "Cele 5 demersuri depuse deja rămân pe traseul de acum."
    keptText(c) {
      const n = (c.usage && c.usage.requests) || 0;
      if (!n) return "";
      return n === 1 ? "Demersul depus deja rămâne pe traseul de acum." : `Cele ${usageText({ requests: n })} depuse deja rămân pe traseul de acum.`;
    },
    usedBy(c) {
      return [...(c.types || []).map((t) => `tipul ${t.name}`), ...(c.causes || []).map((x) => `cauza ${x.name}`)];
    },
    add(key) {
      this.form.steps.push(key);
    },
    drop(i) {
      this.form.steps.splice(i, 1);
      if (!this.form.steps.includes("rector")) this.form.rector_review = false;
    },
    shift(i, dir) {
      const steps = [...this.form.steps];
      [steps[i], steps[i + dir]] = [steps[i + dir], steps[i]];
      this.form.steps = steps;
    },
    openCreate() {
      this.editing = null;
      this.form = { name: "", steps: ["sef"], rector_review: false };
      this.openDialog();
    },
    openEdit(c) {
      this.editing = c;
      this.form = { name: c.name, steps: [...c.steps], rector_review: !!c.rector_review };
      this.openDialog();
    },
    openDialog() {
      this.nameError = "";
      this.dialog = true;
      helper.scrollToTop(this);
    },
    async save() {
      const name = cleanText(this.form.name);
      if (!name) {
        this.nameError = "Denumirea este obligatorie";
        return;
      }
      const steps = [...this.form.steps];
      const review = !!this.form.rector_review && steps.includes("rector");
      const c = this.editing;
      if (!c) {
        const created = await this.change(() => api.createCircuit({ name, steps, rector_review: review }), {
          done: `Circuitul „${name}” a fost adăugat`,
          fallback: "Eroare la adăugarea circuitului",
        });
        if (created) this.dialog = false;
        return;
      }
      const body = {};
      if (name !== c.name) body.name = name;
      if (steps.join() !== c.steps.join()) body.steps = steps;
      if (review !== !!c.rector_review) body.rector_review = review;
      if (!Object.keys(body).length) {
        this.dialog = false;
        return;
      }
      const saved = await this.change(() => api.updateCircuit(c.id, body), {
        done: body.steps || body.rector_review !== undefined
          ? `Circuitul „${name}” a fost salvat: demersurile noi urmează traseul nou`
          : `Circuitul „${name}” a fost salvat`,
        fallback: "Eroare la salvarea circuitului",
      });
      if (saved) this.dialog = false;
    },
    toggleHidden(c) {
      const hide = !c.is_hidden;
      return this.change(() => api.updateCircuit(c.id, { is_hidden: hide }), {
        done: hide ? `Circuitul „${c.name}” este ascuns: nu mai poate fi ales` : `Circuitul „${c.name}” poate fi ales din nou`,
        fallback: "Eroare la salvarea circuitului",
      });
    },
    move(c, dir) {
      const patches = orderPatches(this.sorted, c, dir);
      if (!patches.length) return;
      this.change(
        async () => {
          for (const p of patches) await api.updateCircuit(p.id, { sort_order: p.sort_order });
        },
        { done: "Ordinea a fost salvată", fallback: "Eroare la salvarea ordinii" }
      );
    },
    async remove(c) {
      const users = this.usedBy(c);
      const requests = (c.usage && c.usage.requests) || 0;
      const canDelete = !users.length && !requests;
      let text = "Nu este folosit de niciun tip, cauză sau demers, deci poate fi șters.";
      if (users.length) {
        text = `Este circuitul pentru ${users.join(", ")}. Alegeți alt circuit pentru acestea, apoi îl puteți ascunde sau șterge.`;
      } else if (requests) {
        text = `${requests === 1 ? "Un demers a fost depus" : `Au fost depuse ${usageText({ requests })}`} pe acest circuit. ` +
          (c.is_hidden ? "Este deja ascuns, deci nu mai poate fi ales." : "Ascundeți-l: nu va mai putea fi ales, iar demersurile depuse merg mai departe pe el.");
      }
      const choice = await this.$refs.remove.ask({
        title: canDelete ? `Ștergeți circuitul „${c.name}”?` : `Circuitul „${c.name}” nu poate fi șters`,
        text,
        canDelete,
        canHide: !canDelete && !users.length && !c.is_hidden,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteCircuit(c.id), { done: `Circuitul „${c.name}” a fost șters`, fallback: "Eroare la ștergerea circuitului" });
      } else if (choice === "hide") {
        await this.toggleHidden(c);
      }
    },
  },
};
</script>

<style scoped>
.circuits__path {
  margin-top: 8px;
}
.circuits__for {
  color: var(--ink-3);
  font-size: 0.9rem;
  font-weight: 600;
}
.circuits__steps {
  margin: 0 0 10px;
  padding: 0;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}
.circuits__step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px 8px 12px;
  background: #fff;
  border-bottom: 1px solid var(--line-soft);
}
.circuits__step:last-child {
  border-bottom: 0;
}
.circuits__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--green);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
}
.circuits__icon {
  flex: none;
  color: var(--green) !important;
}
.circuits__text {
  flex: 1 1 auto;
  min-width: 0;
}
.circuits__name {
  display: block;
  font-weight: 700;
  color: var(--ink);
}
.circuits__hint {
  display: block;
  color: var(--ink-3);
  font-size: 0.86rem;
}
.circuits__btns {
  display: flex;
  flex: none;
}
.circuits__none {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--amber-bg);
  color: #6f4508;
  font-weight: 600;
}
.circuits__none .v-icon {
  color: var(--amber) !important;
}
.circuits__add {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.circuits__add-label {
  color: var(--ink-3);
  font-weight: 600;
}
.circuits__add-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}
@media (max-width: 600px) {
  .circuits__hint {
    display: none;
  }
}
</style>
